import {
  type ResourceType,
  type Provider,
  type Condition,
  ConditionOperatorEnum,
} from '@repo/api-gw-sdk';
import type { ColumnDefBase } from '@tanstack/react-table';
import type React from 'react';

import type { MultiSelectConditionPanelProps } from '@/components/conditions/conditionComponents';
import type { FilterProps } from '@/components/queryBuilder/filters';

export function isValueCondition(condition: Condition | undefined): boolean {
  return !!condition && !isCombineCondition(condition);
}

export function isCombineCondition(condition: Condition | undefined): boolean {
  return (
    condition?.operator === ConditionOperatorEnum.And ||
    condition?.operator === ConditionOperatorEnum.Or
  );
}

export const createCombineCondition = (
  conditions: Condition[] = [],
  operator = ConditionOperatorEnum.And
): Condition => ({
  operator,
  conditions,
});

export function createFromCondition(
  property: string,
  value: string
): Condition {
  return {
    operator: ConditionOperatorEnum.From,
    property,
    value: [value],
  };
}

export function createToCondition(property: string, value: string): Condition {
  return {
    operator: ConditionOperatorEnum.To,
    property,
    value: [value],
  };
}

export const createAllIncludedCondition = (): Condition => ({
  property: '',
  operator: '',
  conditions: [],
  value: ['true'],
});

export const createAllExcludedCondition = (): Condition => ({
  property: '',
  operator: '',
  conditions: [],
  value: ['false'],
});

export function removeValueCondition(
  filters: Condition,
  condition: Condition,
  value: string
): Condition {
  const updatedCondition = condition.value?.filter((x) => x !== value) || [];

  const newCondition = updatedCondition.length
    ? { ...condition, value: updatedCondition }
    : undefined;

  return replaceValueCondition(filters, condition, newCondition);
}

function replaceValueCondition(
  filters: Condition,
  oldCondition: Condition,
  newCondition: Condition | undefined
) {
  if (isValueCondition(filters)) {
    return filters;
  }

  return {
    ...filters,
    conditions: filters.conditions?.reduce<Condition[]>((agg, val) => {
      if (val === oldCondition) {
        if (newCondition) {
          agg.push(newCondition);
        }
      } else {
        agg.push(replaceValueCondition(val, oldCondition, newCondition));
      }

      return agg;
    }, []),
  };
}

export function collectValueConditions(condition: Condition): Condition[] {
  return isValueCondition(condition)
    ? [condition]
    : condition.conditions?.flatMap(collectValueConditions) || [];
}

export enum PropertyType {
  Number = 'Number',
  Date = 'Date',
  String = 'String',
  SingleValue = 'SingleValue',
  MultipleValues = 'MultipleValues',
}

export enum PropertyPolicyRelation {
  SingleValue = 'SingleValue',
  MultipleValues = 'MultipleValues',
  String = 'String',
  Tags = 'Tags',
  Boolean = 'Boolean',
}

interface FieldComponentProps<T> {
  entity: T;
  onChange?: () => Promise<void>;
}

export type MultiSelectConditionComponentProps = {
  condition: Condition;
  onChange: (condition: Condition) => void;
  property: FilterProperty;
  supportedProviders?: Provider[];
  supportedResourceTypes?: ResourceType[];
} & Partial<MultiSelectConditionPanelProps>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface FilterProperty<T = any> {
  name: string;
  displayName: string;
  type: PropertyType;
  policyRelation?: PropertyPolicyRelation;
  propertyPredicate?: (providers: Provider[]) => boolean;
  group: string;
  linkedProperties?: FilterProperty[];
  column?: ColumnDefBase<T>;
  accessorFn?: (row: T) => unknown;
  field?: {
    getTitle?: (entity: T) => string;
    Component: (props: FieldComponentProps<T>) => React.ReactNode;
  };
  filter?: {
    fullPanelComponent?: {
      title: string;
      subtitle?: string;
      Component: (
        props: FilterProps & {
          onDoneClick: () => void;
        }
      ) => React.ReactNode;
    };
    expandedByDefault?: boolean;
    Component?: (props: FilterProps) => React.ReactNode;
    Badge?: ({ conditionValue }: { conditionValue: string }) => React.ReactNode;
    shouldHideBadge?: (condition: Condition) => boolean;
  };
  condition?: {
    Component: (props: MultiSelectConditionComponentProps) => React.ReactNode;
  };
}

export const isAnAllCondition = (condition: Condition) => {
  return isEmptyCondition(condition) && condition.value?.length === 1;
};

export const isEmptyCondition = (condition: Condition) => {
  return (
    !condition.property && !condition.operator && !condition.conditions?.length
  );
};

export const hasValidConditionValues = (condition?: Condition): boolean => {
  if (!condition) {
    return false;
  }

  if (condition.value !== undefined) {
    return !!condition.value.length;
  }

  if (condition.conditions?.length) {
    return condition.conditions.every(hasValidConditionValues);
  }

  return false;
};
